import React, { useState } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Users, TrendingUp, TrendingDown, DollarSign, 
  BarChart3, Building2, Crown, CheckCircle2, ArrowUp, ArrowDown,
  X, GitCompare, Sparkles
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Sample IB data for mockup
const sampleIBs = [
  { id: 1, name: 'FGC Trading', health: 92, status: 'excellent', commission: 1062358, lot: 107.8, clients: 481, growth: 13.2, prevCommission: 948000, prevLot: 99.8, prevClients: 418 },
  { id: 2, name: 'FIRA IND', health: 85, status: 'good', commission: 1288664, lot: 116.9, clients: 373, growth: 6.5, prevCommission: 1210000, prevLot: 108.2, prevClients: 352 },
  { id: 3, name: 'SBE', health: 72, status: 'good', commission: 890000, lot: 89.5, clients: 285, growth: 5.4, prevCommission: 845000, prevLot: 82.1, prevClients: 268 },
];

const statusConfig = {
  excellent: { color: '#10B981', gradient: 'from-[#10B981] to-[#059669]', icon: Crown, label: 'EXCELLENT' },
  good: { color: '#3B82F6', gradient: 'from-[#3B82F6] to-[#2563EB]', icon: CheckCircle2, label: 'GOOD' },
};

// Calculate trend
const calcTrend = (current, previous) => {
  const diff = ((current - previous) / previous * 100).toFixed(1);
  return { value: diff, isUp: current >= previous };
};

// ============================================
// MOCKUP A: Card with Performance Trend Indicator
// ============================================
const IBCardWithTrend = ({ ib }) => {
  const config = statusConfig[ib.status];
  const StatusIcon = config.icon;
  
  const commTrend = calcTrend(ib.commission, ib.prevCommission);
  const lotTrend = calcTrend(ib.lot, ib.prevLot);
  const clientTrend = { value: ib.clients - ib.prevClients, isUp: ib.clients >= ib.prevClients };

  return (
    <div className="bg-gradient-to-br from-[#1A1A22] via-[#151519] to-[#121216] border-2 border-[#2A2A35] rounded-2xl overflow-hidden hover:border-[#3A3A45] transition-all">
      {/* Top accent */}
      <div className={`h-1 bg-gradient-to-r ${config.gradient}`} />
      
      {/* Status bar */}
      <div className="px-4 py-2.5 border-b border-[#252530] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
            <StatusIcon size={12} className="text-white" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: config.color }}>
            {config.label}
          </span>
        </div>
        <Badge className="text-[8px] bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30">
          ● ACTIVE
        </Badge>
      </div>

      <div className="p-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl border-2 border-[#2A2A35] bg-[#1E1E26] flex items-center justify-center">
            <Building2 size={22} className="text-[#4A4A5A]" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-[#F0F0F5]">{ib.name}</h3>
              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${config.gradient} flex items-center justify-center text-xs font-bold text-white`}>
                {ib.health}
              </div>
            </div>
            <p className="text-[10px] text-[#6B7280]">Introducing Broker</p>
          </div>
        </div>

        {/* Metrics with TREND INDICATORS */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {/* Commission with trend */}
          <div className="bg-[#1E1E26] rounded-xl p-3 border border-[#2A2A35]">
            <div className="flex items-center gap-1.5 mb-1">
              <DollarSign size={11} className="text-[#10B981]" />
              <span className="text-[8px] uppercase tracking-wider text-[#6B7280] font-semibold">Commission</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-[#E0E0E5]">RM {(ib.commission/1000).toFixed(1)}k</span>
              <div className={`flex items-center gap-0.5 text-[10px] font-semibold ${commTrend.isUp ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                {commTrend.isUp ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                {commTrend.value}%
              </div>
            </div>
            <p className="text-[8px] text-[#4A4A5A] mt-1">vs last month</p>
          </div>

          {/* Traded Lot with trend */}
          <div className="bg-[#1E1E26] rounded-xl p-3 border border-[#2A2A35]">
            <div className="flex items-center gap-1.5 mb-1">
              <BarChart3 size={11} className="text-[#F97316]" />
              <span className="text-[8px] uppercase tracking-wider text-[#6B7280] font-semibold">Traded Lot</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-[#E0E0E5]">{ib.lot}</span>
              <div className={`flex items-center gap-0.5 text-[10px] font-semibold ${lotTrend.isUp ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                {lotTrend.isUp ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                {lotTrend.value}%
              </div>
            </div>
            <p className="text-[8px] text-[#4A4A5A] mt-1">vs last month</p>
          </div>

          {/* Active Clients with trend */}
          <div className="bg-[#1E1E26] rounded-xl p-3 border border-[#2A2A35]">
            <div className="flex items-center gap-1.5 mb-1">
              <Users size={11} className="text-[#8B5CF6]" />
              <span className="text-[8px] uppercase tracking-wider text-[#6B7280] font-semibold">Active Client</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-[#E0E0E5]">{ib.clients}</span>
              <div className={`flex items-center gap-0.5 text-[10px] font-semibold ${clientTrend.isUp ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                {clientTrend.isUp ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                {clientTrend.isUp ? '+' : ''}{clientTrend.value}
              </div>
            </div>
            <p className="text-[8px] text-[#4A4A5A] mt-1">new clients</p>
          </div>

          {/* Growth with mini chart */}
          <div className="bg-[#1E1E26] rounded-xl p-3 border border-[#2A2A35]">
            <div className="flex items-center gap-1.5 mb-1">
              <TrendingUp size={11} className="text-[#10B981]" />
              <span className="text-[8px] uppercase tracking-wider text-[#6B7280] font-semibold">Growth</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-[#10B981]">+{ib.growth}%</span>
              {/* Mini sparkline */}
              <svg width="40" height="16" className="text-[#10B981]">
                <polyline
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  points="0,12 8,10 16,8 24,6 32,4 40,2"
                />
              </svg>
            </div>
            <p className="text-[8px] text-[#4A4A5A] mt-1">trending up</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MOCKUP B: Comparison Mode Panel
// ============================================
const ComparisonPanel = ({ ibs, onClose }) => {
  const metrics = [
    { key: 'health', label: 'Health Score', format: (v) => v, color: '#8B5CF6' },
    { key: 'commission', label: 'Commission', format: (v) => `RM ${(v/1000).toFixed(0)}k`, color: '#10B981' },
    { key: 'lot', label: 'Traded Lot', format: (v) => v.toFixed(1), color: '#F97316' },
    { key: 'clients', label: 'Active Clients', format: (v) => v, color: '#3B82F6' },
    { key: 'growth', label: 'Growth Rate', format: (v) => `+${v}%`, color: '#10B981' },
  ];

  const maxValues = metrics.reduce((acc, m) => {
    acc[m.key] = Math.max(...ibs.map(ib => ib[m.key]));
    return acc;
  }, {});

  return (
    <div className="bg-[#0F0F14] border border-[#252530] rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-[#252530] flex items-center justify-between bg-gradient-to-r from-[#1A1A22] to-transparent">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] flex items-center justify-center">
            <GitCompare size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">IB Comparison</h3>
            <p className="text-xs text-[#6B7280]">Comparing {ibs.length} IBs side by side</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="text-[#6B7280] hover:text-white">
          <X size={18} />
        </Button>
      </div>

      {/* IB Headers */}
      <div className="grid grid-cols-4 gap-3 p-4 border-b border-[#252530]">
        <div className="text-xs font-semibold text-[#6B7280] uppercase">Metric</div>
        {ibs.map(ib => (
          <div key={ib.id} className="text-center">
            <div className={`inline-flex w-10 h-10 rounded-xl bg-gradient-to-br ${statusConfig[ib.status].gradient} items-center justify-center text-sm font-bold text-white mb-2`}>
              {ib.health}
            </div>
            <p className="text-sm font-bold text-white">{ib.name}</p>
            <p className="text-[10px] text-[#6B7280]">{statusConfig[ib.status].label}</p>
          </div>
        ))}
      </div>

      {/* Metrics Comparison */}
      <div className="p-4 space-y-4">
        {metrics.map(metric => (
          <div key={metric.key} className="grid grid-cols-4 gap-3 items-center">
            <div className="text-xs font-semibold text-[#6B7280]">{metric.label}</div>
            {ibs.map(ib => {
              const value = ib[metric.key];
              const percentage = (value / maxValues[metric.key]) * 100;
              const isMax = value === maxValues[metric.key];
              
              return (
                <div key={ib.id} className="relative">
                  <div className="bg-[#1A1A22] rounded-lg p-3 border border-[#252530]">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-sm font-bold ${isMax ? 'text-white' : 'text-[#9CA3AF]'}`}>
                        {metric.format(value)}
                      </span>
                      {isMax && (
                        <Badge className="text-[8px] bg-[#10B981]/20 text-[#10B981] border-0">
                          <Sparkles size={8} className="mr-1" /> Best
                        </Badge>
                      )}
                    </div>
                    {/* Progress bar */}
                    <div className="h-1.5 bg-[#252530] rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${percentage}%`,
                          backgroundColor: isMax ? metric.color : '#4A4A5A'
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Bar Chart Visual */}
      <div className="p-4 border-t border-[#252530]">
        <p className="text-xs font-semibold text-[#6B7280] mb-3 uppercase">Commission Comparison</p>
        <div className="flex items-end justify-around h-32 gap-4">
          {ibs.map(ib => {
            const height = (ib.commission / maxValues.commission) * 100;
            return (
              <div key={ib.id} className="flex flex-col items-center gap-2">
                <div 
                  className={`w-16 rounded-t-lg bg-gradient-to-t ${statusConfig[ib.status].gradient} transition-all duration-500`}
                  style={{ height: `${height}%` }}
                />
                <span className="text-[10px] text-[#6B7280]">{ib.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN MOCKUP PAGE
// ============================================
export default function MockupFeaturesPage() {
  const [showComparison, setShowComparison] = useState(false);

  return (
    <div className="flex min-h-screen bg-[#0A0A0D] text-white">
      <Sidebar />
      
      <main className="flex-1 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">🎨 Visual Mockup - New Features</h1>
            <p className="text-[#6B7280]">Preview of proposed features for LIST OF IB page</p>
          </div>

          {/* MOCKUP A: Performance Trend Indicator */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center text-white font-bold">
                A
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Performance Trend Indicator</h2>
                <p className="text-sm text-[#6B7280]">Setiap metric ada trend arrow & comparison vs bulan lepas</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {sampleIBs.map(ib => (
                <IBCardWithTrend key={ib.id} ib={ib} />
              ))}
            </div>
          </div>

          {/* MOCKUP B: Comparison Mode */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] flex items-center justify-center text-white font-bold">
                  B
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Comparison Mode</h2>
                  <p className="text-sm text-[#6B7280]">Pilih 2-3 IB untuk compare side by side</p>
                </div>
              </div>
              <Button 
                onClick={() => setShowComparison(!showComparison)}
                className="bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white"
              >
                <GitCompare size={16} className="mr-2" />
                {showComparison ? 'Hide Comparison' : 'Show Comparison'}
              </Button>
            </div>
            
            {showComparison && (
              <ComparisonPanel ibs={sampleIBs} onClose={() => setShowComparison(false)} />
            )}
          </div>

          {/* Decision Buttons */}
          <div className="bg-[#151519] border border-[#252530] rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Pilih feature untuk implement:</h3>
            <div className="flex flex-wrap gap-3">
              <Button className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white">
                A - Trend Indicator
              </Button>
              <Button className="bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white">
                B - Comparison Mode
              </Button>
              <Button className="bg-gradient-to-r from-[#E63946] to-[#D62839] text-white">
                Both Features
              </Button>
              <Button variant="outline" className="border-[#252530] text-[#6B7280]">
                Skip - Next Task
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
